import React, { useState, useEffect } from 'react';
import { 
  DollarOutlined, 
  CalendarOutlined,
  DownOutlined,
  SearchOutlined,
  GlobalOutlined,
  TagsOutlined,
  GoogleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { DatePicker, Select, InputNumber, Input, Tag, Alert, Spin } from 'antd';
import api from '../../../../../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const channelTypeOptions = [
  { value: 'SEARCH', label: 'Search - Text ads on Google Search' },
  { value: 'DISPLAY', label: 'Display - Image ads across the web' },
  { value: 'PERFORMANCE_MAX', label: 'Performance Max - AI-optimized across all channels' },
  { value: 'VIDEO', label: 'Video - YouTube ads' },
];

const biddingStrategyOptions = [
  { value: 'MAXIMIZE_CONVERSIONS', label: 'Maximize Conversions' },
  { value: 'TARGET_CPA', label: 'Target CPA (Cost Per Acquisition)' },
  { value: 'TARGET_ROAS', label: 'Target ROAS (Return on Ad Spend)' },
  { value: 'MAXIMIZE_CLICKS', label: 'Maximize Clicks' },
  { value: 'MANUAL_CPC', label: 'Manual CPC' },
];

const deliveryMethodOptions = [
  { value: 'STANDARD', label: 'Standard - Spread budget throughout the day' },
  { value: 'ACCELERATED', label: 'Accelerated - Spend budget as quickly as possible' },
];

const ConfigureGoogleAds = ({ campaignData, onUpdate, onNext }) => {
  
  // Google Ads Account Selection
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(campaignData.googleAds?.selectedAccountId || null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  
  // Campaign Budget
  const [budgetName, setBudgetName] = useState(campaignData.googleAds?.budgetName || '');
  const [dailyBudget, setDailyBudget] = useState(campaignData.googleAds?.dailyBudget || 50);
  const [deliveryMethod, setDeliveryMethod] = useState(campaignData.googleAds?.deliveryMethod || 'STANDARD');

  // Campaign
  const [campaignName, setCampaignName] = useState(campaignData.googleAds?.campaignName || '');
  const [channelType, setChannelType] = useState(campaignData.googleAds?.channelType || 'SEARCH');
  const [biddingStrategy, setBiddingStrategy] = useState(campaignData.googleAds?.biddingStrategy || 'MAXIMIZE_CONVERSIONS');
  const [targetCpa, setTargetCpa] = useState(campaignData.googleAds?.targetCpa || 0);
  const [targetRoas, setTargetRoas] = useState(campaignData.googleAds?.targetRoas || 0);
  const [schedule, setSchedule] = useState(campaignData.googleAds?.schedule || null);
  const [targetGoogleSearch, setTargetGoogleSearch] = useState(campaignData.googleAds?.targetGoogleSearch !== false);
  const [targetSearchNetwork, setTargetSearchNetwork] = useState(campaignData.googleAds?.targetSearchNetwork !== false);
  const [targetContentNetwork, setTargetContentNetwork] = useState(campaignData.googleAds?.targetContentNetwork || false);

  // Ad Group
  const [adGroupName, setAdGroupName] = useState(campaignData.googleAds?.adGroupName || '');
  const [cpcBid, setCpcBid] = useState(campaignData.googleAds?.cpcBid || 1);

  // Keywords
  const [keywords, setKeywords] = useState(campaignData.googleAds?.keywords || []);
  const [keywordInput, setKeywordInput] = useState('');

  // Ad Creative (Responsive Search Ad)
  const [finalUrl, setFinalUrl] = useState(campaignData.googleAds?.finalUrl || '');
  const [path1, setPath1] = useState(campaignData.googleAds?.path1 || '');
  const [path2, setPath2] = useState(campaignData.googleAds?.path2 || '');
  const [headlines, setHeadlines] = useState(campaignData.googleAds?.headlines || ['', '', '']);
  const [descriptions, setDescriptions] = useState(campaignData.googleAds?.descriptions || ['', '']);

  // Targeting - extract addresses from stops or routes
  const getLocationAddresses = () => {
    // For stop-based campaigns, get addresses from stops
    if (campaignData.campaignType === 'stop' && campaignData.selectedStopsData) {
      return campaignData.selectedStopsData
        .map(stop => stop.address || `${stop.coords?.lat}, ${stop.coords?.lng}`)
        .filter(Boolean);
    }
    // For route-based campaigns, get start/end addresses from routes
    if (campaignData.campaignType === 'route' && campaignData.selectedRoutesData) {
      const addresses = [];
      campaignData.selectedRoutesData.forEach(route => {
        if (route.startAddress) addresses.push(route.startAddress);
        if (route.endAddress && route.endAddress !== route.startAddress) addresses.push(route.endAddress);
      });
      return addresses.filter(Boolean);
    }
    // Fallback to selectedLocations
    const selectedLocations = campaignData.selectedLocations || [];
    return selectedLocations.map(loc => loc.name || loc.address || loc).filter(Boolean);
  };
  
  const locationAddresses = getLocationAddresses();
  const locationString = locationAddresses.join('; ');
  const [location] = useState(campaignData.googleAds?.location || locationString);
  const [language, setLanguage] = useState(campaignData.googleAds?.language || 'en');

  // Load connected Google Ads accounts
  useEffect(() => {
    const loadGoogleAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const data = await api.get('/integrations/google-ads/accounts');
        const accounts = Array.isArray(data) ? data : (data.accounts || []);
        setGoogleAccounts(accounts);
        
        // Auto-select first account if none selected
        if (!selectedAccountId && accounts.length > 0) {
          setSelectedAccountId(accounts[0].id);
        }
        setAccountsError(null);
      } catch (error) {
        console.error('Error loading Google Ads accounts:', error);
        setAccountsError('Error loading accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };

    loadGoogleAccounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onUpdate({
      googleAds: {
        selectedAccountId,
        budgetName,
        dailyBudget,
        deliveryMethod,
        campaignName,
        channelType,
        biddingStrategy,
        targetCpa,
        targetRoas,
        schedule,
        targetGoogleSearch,
        targetSearchNetwork,
        targetContentNetwork,
        adGroupName,
        cpcBid,
        keywords,
        finalUrl,
        path1,
        path2,
        headlines,
        descriptions,
        location,
        language,
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, budgetName, dailyBudget, deliveryMethod, campaignName, channelType, biddingStrategy, targetCpa, targetRoas, schedule, targetGoogleSearch, targetSearchNetwork, targetContentNetwork, adGroupName, cpcBid, keywords, finalUrl, path1, path2, headlines, descriptions, location, language]);

  const handleContinue = () => {
    console.log('ConfigureGoogleAds - Continue clicked');
    console.log('Campaign Name:', campaignName);
    console.log('Schedule:', schedule);
    onNext();
  };

  const disabledDate = (current) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setKeywords([...keywords, { text: keywordInput.trim(), matchType: 'PHRASE' }]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const updateHeadline = (index, value) => {
    const newHeadlines = [...headlines];
    newHeadlines[index] = value;
    setHeadlines(newHeadlines);
  };

  const updateDescription = (index, value) => {
    const newDescriptions = [...descriptions];
    newDescriptions[index] = value;
    setDescriptions(newDescriptions);
  };

  return (
    <div className="step-container">
      <div className="sms-header-icon">
        <SearchOutlined />
      </div>
      <h1 className="step-title">Configure Google Ads Campaign</h1>
      <p className="step-subtitle">Set up your Google Search, Display & Performance Max campaign</p>

      {/* Google Ads Account Selection */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px'
      }}>
        <label className="form-label" style={{ 
          fontWeight: 600, 
          fontSize: '14px', 
          color: 'var(--ink)',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <GoogleOutlined style={{ color: '#4285f4' }} />
          Select Google Ads Account
        </label>
        
        {loadingAccounts ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip="Loading your Google Ads accounts..." />
          </div>
        ) : accountsError ? (
          <Alert
            message="Error Loading Accounts"
            description={accountsError}
            type="error"
            showIcon
            icon={<WarningOutlined />}
          />
        ) : googleAccounts.length === 0 ? (
          <Alert
            message="No Google Ads Accounts Connected"
            description={
              <span>
                Please connect your Google Ads account first in{' '}
                <a href="/client/integrations" style={{ fontWeight: 600 }}>Integrations</a>
              </span>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />
        ) : (
          <Select
            style={{ width: '100%', height: 48 }}
            value={selectedAccountId}
            onChange={setSelectedAccountId}
            placeholder="Choose which Google Ads account to use for this campaign"
            size="large"
          >
            {googleAccounts.map((account) => (
              <Select.Option key={account.id} value={account.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <GoogleOutlined style={{ color: '#4285f4', fontSize: '16px' }} />
                  <div>
                    <div style={{ fontWeight: 500 }}>
                      {account.name || 'Google Ads Account'}
                    </div>
                    {account.email && (
                      <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                        {account.email}
                      </div>
                    )}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      {/* Campaign Configuration - 3 Column Grid with Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Budget Name Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Budget Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Route Campaign Budget"
            value={budgetName}
            onChange={(e) => setBudgetName(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* Campaign Name Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Campaign Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Route-Based Search Campaign"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* Channel Type Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Channel Type</label>
          <Select
            style={{ width: '100%', height: 44 }}
            value={channelType}
            onChange={setChannelType}
            options={channelTypeOptions}
          />
        </div>
      </div>

      {/* Budget & Strategy Configuration - 3 Column Grid with Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Daily Budget Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            <DollarOutlined /> Daily Budget (USD)
          </label>
          <InputNumber
            style={{ width: '100%', height: 44 }}
            min={1}
            value={dailyBudget}
            onChange={setDailyBudget}
            prefix="$"
            placeholder="Daily budget"
          />
        </div>

        {/* Bidding Strategy Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>Bidding Strategy</label>
          <Select
            style={{ width: '100%', height: 44 }}
            value={biddingStrategy}
            onChange={setBiddingStrategy}
            options={biddingStrategyOptions}
          />
        </div>

        {/* Campaign Schedule Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ 
            fontWeight: 600, 
            fontSize: '13px', 
            color: 'var(--ink)',
            marginBottom: '8px',
            display: 'block'
          }}>
            <CalendarOutlined /> Campaign Schedule
          </label>
          <RangePicker
            style={{ width: '100%', height: 44 }}
            value={schedule}
            onChange={setSchedule}
            format="MMM D, YYYY"
            placeholder={['Start Date', 'End Date']}
            disabledDate={disabledDate}
          />
        </div>
      </div>

      {/* Additional Settings - Full Width Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
        width: 'calc(100% + 0px)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>Additional Settings</div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
          <div>
            <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Delivery Method</label>
            <Select
              style={{ width: '100%', height: 44 }}
              value={deliveryMethod}
              onChange={setDeliveryMethod}
              options={deliveryMethodOptions}
            />
          </div>
          
          {biddingStrategy === 'TARGET_CPA' && (
            <div>
              <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Target CPA (USD)</label>
              <InputNumber
                style={{ width: '100%', height: 44 }}
                min={0}
                value={targetCpa}
                onChange={setTargetCpa}
                prefix="$"
                placeholder="Target cost per acquisition"
              />
            </div>
          )}

          {biddingStrategy === 'TARGET_ROAS' && (
            <div>
              <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Target ROAS (%)</label>
              <InputNumber
                style={{ width: '100%', height: 44 }}
                min={0}
                max={1000}
                value={targetRoas}
                onChange={setTargetRoas}
                placeholder="Target return on ad spend"
              />
            </div>
          )}

          {channelType === 'SEARCH' && (
            <div>
              <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Network Targeting</label>
              <Select
                mode="multiple"
                style={{ width: '100%', minHeight: 44 }}
                placeholder="Select networks"
                value={[
                  ...(targetGoogleSearch ? ['google_search'] : []),
                  ...(targetSearchNetwork ? ['search_partners'] : []),
                  ...(targetContentNetwork ? ['display_network'] : [])
                ]}
                onChange={(values) => {
                  setTargetGoogleSearch(values.includes('google_search'));
                  setTargetSearchNetwork(values.includes('search_partners'));
                  setTargetContentNetwork(values.includes('display_network'));
                }}
                options={[
                  { value: 'google_search', label: 'Google Search' },
                  { value: 'search_partners', label: 'Search Network Partners' },
                  { value: 'display_network', label: 'Display Network' }
                ]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Location Targeting - Full Width Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
        width: 'calc(100% + 0px)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>
          <GlobalOutlined /> Location Targeting
        </div>
        {locationAddresses.length > 0 ? (
          <div style={{ 
            background: '#f0f9ff', 
            border: '1px solid #bae6fd', 
            borderRadius: 8, 
            padding: 16,
            maxHeight: 300,
            overflowY: 'auto'
          }}>
            <div style={{ fontSize: 13, color: '#0369a1', marginBottom: 12, fontWeight: 500 }}>
              {locationAddresses.length} location{locationAddresses.length > 1 ? 's' : ''} targeted:
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(5, 1fr)', 
              gap: '12px',
              alignItems: 'start'
            }}>
              {locationAddresses.map((addr, idx) => (
                <div key={idx} style={{ 
                  background: '#fff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: 13, 
                  color: '#0c4a6e',
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ fontWeight: 500, marginBottom: '2px', color: '#0369a1' }}>
                    #{idx + 1}
                  </div>
                  {addr}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ 
            background: '#fef3c7', 
            border: '1px solid #fbbf24', 
            borderRadius: 8, 
            padding: 12,
            fontSize: 14,
            color: '#92400e'
          }}>
            ⚠️ No locations selected. Please select stops or routes in previous steps.
          </div>
        )}
        <input type="hidden" value={location} />
      </div>

      {/* Language, Ad Group, Keywords - 3 Column Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Language Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Language</label>
          <Select
            style={{ width: '100%', height: 44 }}
            value={language}
            onChange={setLanguage}
          >
            <Select.Option value="en">English</Select.Option>
            <Select.Option value="es">Spanish</Select.Option>
            <Select.Option value="fr">French</Select.Option>
            <Select.Option value="de">German</Select.Option>
          </Select>
        </div>

        {/* Ad Group Name Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Ad Group Name</label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g., Main Ad Group"
            value={adGroupName}
            onChange={(e) => setAdGroupName(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* CPC Bid or Keywords Card */}
        {biddingStrategy === 'MANUAL_CPC' ? (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease'
          }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>CPC Bid (USD)</label>
            <InputNumber
              style={{ width: '100%', height: 44 }}
              min={0.01}
              step={0.1}
              value={cpcBid}
              onChange={setCpcBid}
              prefix="$"
              placeholder="Cost per click bid"
            />
          </div>
        ) : channelType === 'SEARCH' ? (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease'
          }}>
            <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>
              <TagsOutlined /> Keywords ({keywords.length})
            </label>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                type="text"
                className="form-input"
                placeholder="Enter keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                style={{
                  flex: 1,
                  borderRadius: '8px',
                  border: '1px solid var(--paper-300)',
                  padding: '10px 12px',
                  height: '44px'
                }}
              />
              <button className="cooldown-btn" onClick={addKeyword} style={{ height: '44px', padding: '0 12px' }}>+</button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Keywords Display - Full Width */}
      {channelType === 'SEARCH' && keywords.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          marginBottom: '24px',
          width: 'calc(100% + 0px)'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {keywords.map((kw, index) => (
              <Tag
                key={index}
                closable
                onClose={() => removeKeyword(index)}
                style={{ fontSize: 14, padding: '4px 8px' }}
              >
                {kw.text} ({kw.matchType})
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Final URL and Paths - 3 Column Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        
        {/* Final URL Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Final URL</label>
          <input
            type="text"
            className="form-input"
            placeholder="https://example.com/landing-page"
            value={finalUrl}
            onChange={(e) => setFinalUrl(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* Path 1 Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Path 1 (Optional)</label>
          <input
            type="text"
            className="form-input"
            placeholder="service"
            value={path1}
            onChange={(e) => setPath1(e.target.value)}
            maxLength={15}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>

        {/* Path 2 Card */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease'
        }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Path 2 (Optional)</label>
          <input
            type="text"
            className="form-input"
            placeholder="area"
            value={path2}
            onChange={(e) => setPath2(e.target.value)}
            maxLength={15}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--paper-300)',
              padding: '10px 12px'
            }}
          />
        </div>
      </div>

      {/* Headlines and Descriptions - Full Width Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
        width: 'calc(100% + 0px)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '15px', fontWeight: 600, color: 'var(--ink)' }}>Ad Creative (Responsive Search Ad)</div>
        
        {/* Headlines */}
        <div style={{ marginBottom: '20px' }}>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Headlines (3-15 required, max 30 chars each)</label>
          {headlines.map((headline, index) => (
            <input
              key={index}
              type="text"
              className="form-input"
              placeholder={`Headline ${index + 1}`}
              value={headline}
              onChange={(e) => updateHeadline(index, e.target.value)}
              maxLength={30}
              style={{ 
                marginBottom: 8,
                width: '100%',
                borderRadius: '8px',
                border: '1px solid var(--paper-300)',
                padding: '10px 12px'
              }}
            />
          ))}
          {headlines.length < 15 && (
            <button 
              className="cooldown-btn" 
              onClick={() => setHeadlines([...headlines, ''])}
              style={{ marginTop: 8 }}
            >
              Add Headline
            </button>
          )}
        </div>

        {/* Descriptions */}
        <div>
          <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '8px', display: 'block' }}>Descriptions (2-4 required, max 90 chars each)</label>
          {descriptions.map((desc, index) => (
            <TextArea
              key={index}
              className="form-textarea"
              placeholder={`Description ${index + 1}`}
              value={desc}
              onChange={(e) => updateDescription(index, e.target.value)}
              maxLength={90}
              rows={2}
              style={{ 
                marginBottom: 8,
                width: '100%',
                borderRadius: '8px',
                border: '1px solid var(--paper-300)',
                padding: '10px 12px'
              }}
            />
          ))}
          {descriptions.length < 4 && (
            <button 
              className="cooldown-btn" 
              onClick={() => setDescriptions([...descriptions, ''])}
              style={{ marginTop: 8 }}
            >
              Add Description
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          Configure Google Ads settings
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={!selectedAccountId || !campaignName || !schedule || googleAccounts.length === 0}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default ConfigureGoogleAds;
